import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import axiosInstance from "../../../utils/axiosInstance";
import { getTenantId } from "../../../utils/auth";
import { getTenantFromToken } from "../../../utils/utils";

declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB: any;
    gapi: any;
    google: any;
  }
}

interface IntegrationStatus {
  facebook: "idle" | "loading" | "connected" | "error";
  google: "idle" | "loading" | "connected" | "error";
  twitter: "idle" | "loading" | "connected" | "error";
  universalAds: "idle" | "loading" | "connected" | "error";
}

const tenantId = getTenantFromToken();

const MediaIntegration: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<IntegrationStatus>({
    facebook: "idle",
    google: "idle",
    twitter: "idle",
    universalAds: "idle",
  });
  // Load integration status from backend on component mount
  const loadIntegrationStatus = async () => {
    console.log('Loading integration status from backend...');

    try {
      // Call the unified status endpoint (same as HubSpot)
      const response = await axiosInstance.get('/api/integrations/status');

      console.log('Status API response:', response.data);

      if (response.data && response.data.integrations) {
        const integrations = response.data.integrations;

        // Update Facebook status
        if (integrations.facebook) {
          const facebookStatus = integrations.facebook.status;
          console.log('Facebook status from API:', facebookStatus);

          setStatus(prev => ({
            ...prev,
            facebook: facebookStatus === 'active' ? 'connected' : 'idle'
          }));

          console.log('Updated Facebook status to:', facebookStatus === 'active' ? 'connected' : 'idle');
        } else {
          console.log('No Facebook integration found');
          setStatus(prev => ({ ...prev, facebook: 'idle' }));
        }

        // Update Google status if available
        if (integrations.google) {
          const googleStatus = integrations.google.status;
          setStatus(prev => ({
            ...prev,
            google: googleStatus === 'active' ? 'connected' : 'idle'
          }));
        }

        // Update Twitter status if available
        if (integrations.twitter) {
          const twitterStatus = integrations.twitter.status;
          setStatus(prev => ({
            ...prev,
            twitter: twitterStatus === 'active' ? 'connected' : 'idle'
          }));
        }

        // Update Universal Ads status if available
        if (integrations.universal_ads) {
          const uaStatus = integrations.universal_ads.status;
          setStatus(prev => ({
            ...prev,
            universalAds: uaStatus === 'active' ? 'connected' : 'idle'
          }));
        }
      } else {
        console.log('No integrations data in response');
        setStatus(prev => ({ ...prev, facebook: 'idle' }));
      }
    } catch (error) {
      console.error('Error loading integration status:', error);
      // Don't set to error - just leave as idle if we can't load status
      setStatus(prev => ({ ...prev, facebook: 'idle' }));
    }
  };

  // Load integration status when component mounts
  useEffect(() => {
    console.log('Component mounted - loading integration status');
    loadIntegrationStatus();
  }, []);

  // Handle Universal Ads OAuth callback query params (hash-route aware)
  useEffect(() => {
    const uaResult = searchParams.get("universal_ads");

    if (uaResult) {
      if (uaResult === "success") {
        setStatus((prev) => ({ ...prev, universalAds: "connected" }));
        // Reload to confirm from backend
        loadIntegrationStatus();
      } else if (uaResult === "declined") {
        setStatus((prev) => ({ ...prev, universalAds: "idle" }));
      } else if (uaResult === "error") {
        console.error("Universal Ads OAuth error:", searchParams.get("error"));
        setStatus((prev) => ({ ...prev, universalAds: "error" }));
      }

      // Clean up query params from hash route URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    window.fbAsyncInit = function () {
      try {
        window.FB.init({
          appId: "3120378421464026",
          cookie: true,
          xfbml: true,
          version: "v17.0",
        });

        window.FB.getLoginStatus((response: any) => {
          if (response.status === "connected") {
            setStatus((prev) => ({ ...prev, facebook: "connected" }));
          }
        });
      } catch (error) {
        console.error("Facebook SDK initialization error", error);
      }
    };

    const loadFB = () => {
      const existingScript = document.getElementById("facebook-jssdk");
      if (existingScript) return;

      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.onerror = () => {
        console.error("Failed to load Facebook SDK script.");
        setStatus((prev) => ({ ...prev, facebook: "error" }));
      };
      document.body.appendChild(script);
    };

    loadFB();
  }, []);

  const handleFacebookLogin = () => {
    setStatus((prev) => ({ ...prev, facebook: "loading" }));

    window.FB.login(
      (response: any) => {
        if (response.authResponse) {
          const { accessToken, expiresIn, userID } = response.authResponse;

          // Call API without await - handle as promise
          axiosInstance.post("/api/integrations/facebook/auth", {
            tenantId: getTenantId(),
            client_id: userID,
            access_token: accessToken,
            expires_in: expiresIn,
          })
          .then(() => {
            setStatus((prev) => ({ ...prev, facebook: "connected" }));
            // Reload integration status from backend to confirm persistence
            setTimeout(() => {
              loadIntegrationStatus();
            }, 1000);
          })
          .catch((error) => {
            console.error("Error saving Facebook token", error);
            setStatus((prev) => ({ ...prev, facebook: "error" }));
          });
        } else {
          setStatus((prev) => ({ ...prev, facebook: "error" }));
        }
      },
      { scope: "business_management,ads_management,email,public_profile" }
    );
  };

  const handleGoogleLogin = async () => {
    setStatus((prev) => ({ ...prev, google: "loading" }));
console.log("hello google")
    try {
      const authInstance = window.gapi.auth2.getAuthInstance();
      const user = await authInstance.signIn({
        scope:
          "https://www.googleapis.com/auth/adwords https://www.googleapis.com/auth/analytics.readonly",
      });

      const accessToken = user.getAuthResponse().access_token;
      const profile = user.getBasicProfile();

      await axiosInstance.post("/api/google/auth", {
        tenantId: getTenantId(),
        client_id: profile.getId(),
        access_token: accessToken,
        email: profile.getEmail(),
      });

      setStatus((prev) => ({ ...prev, google: "connected" }));
    } catch (error) {
      console.error("Error with Google login", error);
      setStatus((prev) => ({ ...prev, google: "error" }));
    }
  };

  const handleTwitterLogin = async () => {
    setStatus((prev) => ({ ...prev, twitter: "loading" }));

    try {
      // Simulate Twitter OAuth flow
      // In a real implementation, you'd redirect to Twitter's OAuth endpoint
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Mock successful Twitter auth
      await axiosInstance.post("/api/twitter/auth", {
        tenantId: getTenantId(),
        oauth_token: "mock_oauth_token",
        oauth_token_secret: "mock_oauth_token_secret",
        user_id: "mock_twitter_user_id",
      });

      setStatus((prev) => ({ ...prev, twitter: "connected" }));
    } catch (error) {
      console.error("Error with Twitter login", error);
      setStatus((prev) => ({ ...prev, twitter: "error" }));
    }
  };

  const handleUniversalAdsLogin = async () => {
    setStatus((prev) => ({ ...prev, universalAds: "loading" }));

    try {
      // Call backend to get the UA authorization URL and nonce
      const response = await axiosInstance.get(
        "/api/integrations/universal-ads/initiate-oauth"
      );

      if (response.data?.authorize_url) {
        // Store nonce in a cookie so the backend callback can recover tenant_id.
        // UA redirects to ops.adrsta.ai (shared host), so we need the nonce to
        // look up which tenant initiated the OAuth flow in DynamoDB.
        if (response.data.nonce) {
          document.cookie = `ua_oauth_nonce=${response.data.nonce}; path=/; domain=.adrsta.ai; max-age=600; SameSite=Lax; Secure`;
        }

        // Redirect user to Universal Ads authorization page
        window.location.href = response.data.authorize_url;
      } else {
        console.error("No authorize_url returned from backend");
        setStatus((prev) => ({ ...prev, universalAds: "error" }));
      }
    } catch (error) {
      console.error("Error initiating Universal Ads OAuth", error);
      setStatus((prev) => ({ ...prev, universalAds: "error" }));
    }
  };

  const getStatusIcon = (platformStatus: string) => {
    switch (platformStatus) {
      case "loading":
        return <Loader2 className="w-5 h-5 animate-spin text-blue-500" />;
      case "connected":
        return <Check className="w-5 h-5 text-green-500" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getButtonText = (platform: string, platformStatus: string) => {
    switch (platformStatus) {
      case "loading":
        return "Connecting...";
      case "connected":
        return "Connected";
      case "error":
        return "Retry Connection";
      default:
        return `Connect ${platform}`;
    }
  };

  const getButtonStyle = (platformStatus: string, baseColor: string) => {
    switch (platformStatus) {
      case "connected":
        return "bg-green-500 hover:bg-green-600 cursor-default";
      case "error":
        return "bg-red-500 hover:bg-red-600";
      case "loading":
        return `${baseColor} opacity-75 cursor-not-allowed`;
      default:
        return `${baseColor} hover:opacity-90`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Social Media Integration
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Connect your social media accounts to unlock powerful marketing
            insights and automation capabilities.
          </p>
        </div>

        {/* Integration Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Hide for Universal Ads */}
          { tenantId !== "2e332294-8271-4a0e-8391-8b635f86a27e" && (
            <>
              {/* Facebook Integration */}
              <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-6 bg-blue-50 rounded-full flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-blue-600"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Facebook
                  </h3>
                  <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                    Access Facebook Ads Manager, Business Manager, and page insights
                    to optimize your marketing campaigns.
                  </p>
                  <div className="flex items-center justify-center mb-4">
                    {getStatusIcon(status.facebook)}
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      {status.facebook === "connected"
                        ? "Successfully Connected"
                        : status.facebook === "error"
                        ? "Connection Failed"
                        : status.facebook === "loading"
                        ? "Connecting..."
                        : "Not Connected"}
                    </span>
                  </div>
                  <button
                    onClick={() => handleFacebookLogin()}
                    disabled={
                      status.facebook === "loading" ||
                      status.facebook === "connected"
                    }
                    className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(
                      status.facebook,
                      "bg-blue-600 hover:bg-blue-700"
                    )}`}
                  >
                    {getButtonText("Facebook", status.facebook)}
                  </button>
                </div>
              </div>
              {/* <MainDashboard /> */}
              {/* Google Integration */}
              <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-red-600"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Google</h3>
                  <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                    Connect Google Ads and Analytics to track performance and
                    optimize your digital marketing ROI.
                  </p>
                  <div className="flex items-center justify-center mb-4">
                    {getStatusIcon(status.google)}
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      {status.google === "connected"
                        ? "Successfully Connected"
                        : status.google === "error"
                        ? "Connection Failed"
                        : status.google === "loading"
                        ? "Connecting..."
                        : "Not Connected"}
                    </span>
                  </div>
                  <button
                    onClick={() => handleGoogleLogin()}
                    disabled={
                      status.google === "loading" || status.google === "connected"
                    }
                    className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(
                      status.google,
                      "bg-red-600 hover:bg-red-700"
                    )}`}
                  >
                    {getButtonText("Google", status.google)}
                  </button>
                </div>
              </div>

              {/* Twitter Integration */}
              <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto mb-6 bg-sky-50 rounded-full flex items-center justify-center">
                    <svg
                      className="w-10 h-10 text-sky-600"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Twitter</h3>
                  <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                    Integrate Twitter to manage your tweets, analyze engagement, and
                    track brand mentions across the platform.
                  </p>
                  <div className="flex items-center justify-center mb-4">
                    {getStatusIcon(status.twitter)}
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      {status.twitter === "connected"
                        ? "Successfully Connected"
                        : status.twitter === "error"
                        ? "Connection Failed"
                        : status.twitter === "loading"
                        ? "Connecting..."
                        : "Not Connected"}
                    </span>
                  </div>
                  <button
                    onClick={() => handleTwitterLogin()}
                    disabled={
                      status.twitter === "loading" || status.twitter === "connected"
                    }
                    className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(
                      status.twitter,
                      "bg-sky-600 hover:bg-sky-700"
                    )}`}
                  >
                    {getButtonText("Twitter", status.twitter)}
                  </button>
                </div>
              </div>
            </>
          )}
          

          {/* Universal Ads Integration */}
          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:scale-105 transition-all duration-300 border border-gray-100">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-purple-50 rounded-full flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Universal Ads
              </h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Connect Universal Ads to sync campaign spend data, geo reports,
                and performance metrics for MMM and Geo Lift analysis.
              </p>
              <div className="flex items-center justify-center mb-4">
                {getStatusIcon(status.universalAds)}
                <span className="ml-2 text-sm font-medium text-gray-700">
                  {status.universalAds === "connected"
                    ? "Successfully Connected"
                    : status.universalAds === "error"
                    ? "Connection Failed"
                    : status.universalAds === "loading"
                    ? "Connecting..."
                    : "Not Connected"}
                </span>
              </div>
              <button
                onClick={() => handleUniversalAdsLogin()}
                disabled={
                  status.universalAds === "loading" ||
                  status.universalAds === "connected"
                }
                className={`w-full py-3 px-6 rounded-xl text-white font-semibold transition-all duration-200 ${getButtonStyle(
                  status.universalAds,
                  "bg-purple-600 hover:bg-purple-700"
                )}`}
              >
                {getButtonText("Universal Ads", status.universalAds)}
              </button>
            </div>
          </div>
        </div>

        {/* Features Section */}
        {/* <div className="mt-16 bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            What You Can Do
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Analytics & Insights</h3>
              <p className="text-gray-600">Get comprehensive analytics across all your social media platforms in one unified dashboard.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Automated Campaigns</h3>
              <p className="text-gray-600">Create and manage automated marketing campaigns across multiple social media platforms.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Social Listening</h3>
              <p className="text-gray-600">Monitor brand mentions, track sentiment, and engage with your audience in real-time.</p>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default MediaIntegration;
